import { useEffect, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Image,
  Input,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  Upload,
} from "antd";
import type { UploadFile } from "antd";
import { PictureOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DataTable, useTableParams } from "../../components/DataTable";
import { RelativeTime, StatusTag } from "../../components/format";
import { api } from "../../services/api";
import { useAuth } from "../../services/auth";
import { useCharacter } from "../../services/character";

const FILTERS = ["requesterKind", "mode", "status"];

type GenerationProviderId = "aliyun-bailian" | "volcengine-jimeng";
type GenerationProviderSelection = "product-default" | GenerationProviderId;

interface ProviderConfiguration {
  id: GenerationProviderId;
  model: string;
  configured: boolean;
  priceCnyPerImage: number | null;
  maxReferenceImages: number;
  maxPromptLength: number;
  supportsReference4K: boolean;
  supportsThinkingMode: boolean;
  thinkingModeRequiresNoReferences: boolean;
  supportsWatermark: boolean;
}

interface GenerationConfiguration {
  provider: GenerationProviderId;
  providerSource: "database" | "environment";
  environmentProvider: GenerationProviderId;
  model: string | null;
  providerConfigured: boolean;
  storageConfigured: boolean;
  priceCnyPerImage: number | null;
  maxReferenceImages: number;
  maxPromptLength: number;
  supportsReference4K: boolean;
  supportsThinkingMode: boolean;
  supportsWatermark: boolean;
  outputCount: number;
  defaultStyleReference: {
    backgroundImageId: string;
    assetId: string;
    displayName: string;
  } | null;
  providers: ProviderConfiguration[];
}

interface GenerationResult {
  id: string;
  status: "succeeded";
  outputAssetId: string;
  outputAssetUrl: string | null;
  provider: string;
  model: string;
  providerRequestId: string | null;
  costCents: number;
  latencyMs: number;
  totalLatencyMs: number;
}

interface PersistenceRecoveryResult {
  id: string;
  status: "succeeded" | "running";
  outputAssetId: string | null;
  outputAssetUrl: string | null;
  chargedAgain: false;
  alreadyRecovered: boolean;
}

const providerLabel = (provider: string): string =>
  provider === "volcengine-jimeng"
    ? "火山引擎即梦"
    : provider === "aliyun-bailian"
      ? "阿里云百炼"
      : provider;

const modelOptionLabel = (provider: ProviderConfiguration): string =>
  `${provider.id === "volcengine-jimeng" ? "即梦 4.6" : "百炼万相 2.7 Pro"} · ${provider.model}${provider.configured ? "" : "（未配置）"}`;

const wasPersistenceRecovered = (metadata: unknown): boolean =>
  Boolean(
    metadata &&
      typeof metadata === "object" &&
      !Array.isArray(metadata) &&
      (metadata as Record<string, unknown>).persistenceRecovered,
  );

interface GenerationRow {
  id: string;
  requesterKind: "admin" | "user" | "system";
  requesterName: string | null;
  requesterUserId: string | null;
  character: { id: string; displayName: string } | null;
  mode: "character" | "general";
  provider: string;
  model: string;
  status: "queued" | "running" | "succeeded" | "failed";
  requestPrompt: string;
  effectivePrompt: string | null;
  referenceInputs: unknown;
  settings: unknown;
  outputAsset: {
    id: string;
    mimeType: string;
    sizeBytes: number;
    status: string;
    publicUrl: string | null;
  } | null;
  providerRequestId: string | null;
  providerMetadata: unknown;
  recoveryAvailable: boolean;
  recoveryInProgress: boolean;
  costCents: number;
  latencyMs: number;
  totalLatencyMs: number;
  errorCode: string | null;
  errorMessage: string | null;
  reason: string | null;
  createdAt: string;
}

interface GenerationForm {
  characterId: string;
  mode: "character" | "general";
  prompt: string;
  stylePrompt: string;
  size: "1K" | "2K" | "4K";
  watermark: boolean;
  thinkingMode: boolean;
  referencePurpose: "composition" | "style";
  reason: string;
}

function thumbnailUrl(publicUrl: string, width: number): string {
  const pixels = Math.max(64, Math.ceil(width * 2));
  const separator = publicUrl.includes("?") ? "&" : "?";
  return `${publicUrl}${separator}x-oss-process=image/resize,m_fill,w_${pixels},h_${pixels}`;
}

function AuthenticatedAssetImage({
  assetId,
  width = 72,
  publicUrl = null,
}: {
  assetId: string;
  width?: number;
  publicUrl?: string | null;
}) {
  const [blobThumbnailSrc, setBlobThumbnailSrc] = useState<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewRequested, setPreviewRequested] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [directFailed, setDirectFailed] = useState(false);
  const useDirectUrl = Boolean(publicUrl && !directFailed);
  const directThumbnailSrc = useDirectUrl && publicUrl ? thumbnailUrl(publicUrl, width) : null;
  const thumbnail = useQuery({
    queryKey: ["adminAssetBlob", assetId, "thumbnail", width],
    queryFn: async () => {
      const blob = await api.blob(
        `/api/v1/admin/assets/${assetId}/content?size=${Math.ceil(width)}`,
      );
      if (blob.size === 0) throw new Error("图片文件为空");
      return blob;
    },
    enabled: !useDirectUrl,
    staleTime: 5 * 60_000,
    refetchOnMount: "always",
    retry: (failureCount, queryError) => {
      const status = (queryError as { status?: number }).status;
      return status !== 401 && status !== 403 && failureCount < 4;
    },
    retryDelay: (attempt) => Math.min(750 * 2 ** attempt, 6_000),
  });
  const original = useQuery({
    queryKey: ["adminAssetBlob", assetId, "original"],
    queryFn: async () => {
      const blob = await api.blob(`/api/v1/admin/assets/${assetId}/content`);
      if (blob.size === 0) throw new Error("图片文件为空");
      return blob;
    },
    enabled: previewRequested && !useDirectUrl,
    staleTime: 5 * 60_000,
    retry: 2,
  });

  useEffect(() => {
    if (!thumbnail.data) return;
    const url = URL.createObjectURL(thumbnail.data);
    setBlobThumbnailSrc(url);
    return () => {
      URL.revokeObjectURL(url);
      setBlobThumbnailSrc(null);
    };
  }, [thumbnail.data]);

  useEffect(() => {
    if (!original.data) return;
    const url = URL.createObjectURL(original.data);
    setPreviewSrc(url);
    setPreviewVisible(true);
    return () => {
      URL.revokeObjectURL(url);
      setPreviewSrc(null);
    };
  }, [original.data]);

  useEffect(() => {
    setDirectFailed(false);
    setPreviewRequested(false);
    setPreviewVisible(false);
  }, [publicUrl]);

  const src = directThumbnailSrc ?? blobThumbnailSrc;

  if (!useDirectUrl && thumbnail.isError && !src) {
    return (
      <button
        type="button"
        aria-label={`图片加载失败，点击重试：${thumbnail.error.message}`}
        title={`${thumbnail.error.message}；点击重试`}
        onClick={() => void thumbnail.refetch()}
        style={{
          width,
          height: width,
          display: "grid",
          placeItems: "center",
          gap: 4,
          padding: 4,
          border: "1px dashed var(--miao-color-border)",
          background: "var(--miao-color-fill)",
          borderRadius: 6,
          color: "var(--miao-color-text-secondary)",
          cursor: "pointer",
        }}
      >
        <ReloadOutlined spin={thumbnail.isFetching} />
        {width >= 120 && <span>加载失败，点击重试</span>}
      </button>
    );
  }

  if (!src) {
    return (
      <div
        style={{
          width,
          height: width,
          display: "grid",
          placeItems: "center",
          background: "var(--miao-color-fill)",
          borderRadius: 6,
          color: "var(--miao-color-text-secondary)",
        }}
      >
        {thumbnail.isLoading || thumbnail.isFetching ? <Spin size="small" /> : <PictureOutlined />}
      </div>
    );
  }
  return (
    <Image
      alt="生图结果"
      src={src}
      width={width}
      height={width}
      preview={{
        mask: original.isFetching ? "正在加载原图…" : width >= 120 ? "点击查看大图" : "查看",
        src: useDirectUrl && publicUrl ? publicUrl : previewSrc ?? src,
        visible: previewVisible,
        onVisibleChange: (visible) => {
          if (!visible) {
            setPreviewVisible(false);
            return;
          }
          if (useDirectUrl || previewSrc) {
            setPreviewVisible(true);
            return;
          }
          setPreviewRequested(true);
          if (original.isError) void original.refetch();
        },
      }}
      onError={() => {
        setPreviewVisible(false);
        if (useDirectUrl) {
          setDirectFailed(true);
        } else {
          setBlobThumbnailSrc(null);
          void thumbnail.refetch();
        }
      }}
      style={{ objectFit: "cover", borderRadius: 6, cursor: "zoom-in" }}
    />
  );
}

export function ImageGenerationsPage() {
  const { message } = App.useApp();
  const { can } = useAuth();
  const { characters, characterId, setCharacterId } = useCharacter();
  const queryClient = useQueryClient();
  const { searchParams, setParam } = useTableParams(FILTERS);
  const [form] = Form.useForm<GenerationForm>();
  const mode = Form.useWatch("mode", form);
  const [referenceFiles, setReferenceFiles] = useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [retryingPersistenceId, setRetryingPersistenceId] = useState<string | null>(null);
  const [latest, setLatest] = useState<GenerationResult | null>(null);
  const [generationProviderSelection, setGenerationProviderSelection] =
    useState<GenerationProviderSelection>("product-default");
  const [defaultProviderDraft, setDefaultProviderDraft] =
    useState<GenerationProviderId | null>(null);
  const [savingDefaultProvider, setSavingDefaultProvider] = useState(false);
  const latestResultRef = useRef<HTMLDivElement>(null);

  const configuration = useQuery({
    queryKey: ["imageGenerationConfiguration"],
    queryFn: () =>
      api.get<GenerationConfiguration>("/api/v1/admin/image-generations/configuration"),
  });
  const selectedProviderId =
    generationProviderSelection === "product-default"
      ? configuration.data?.provider
      : generationProviderSelection;
  const selectedProvider = configuration.data?.providers.find(
    (provider) => provider.id === selectedProviderId,
  );

  useEffect(() => {
    if (!defaultProviderDraft && configuration.data) {
      setDefaultProviderDraft(configuration.data.provider);
    }
  }, [configuration.data, defaultProviderDraft]);

  useEffect(() => {
    if (characterId) form.setFieldValue("characterId", characterId);
  }, [characterId, form]);

  useEffect(() => {
    const usesReference = mode === "character" || referenceFiles.length > 0;
    if (
      usesReference &&
      selectedProvider?.supportsReference4K === false &&
      form.getFieldValue("size") === "4K"
    ) {
      form.setFieldValue("size", "2K");
    }
  }, [
    form,
    mode,
    referenceFiles.length,
    selectedProvider?.supportsReference4K,
  ]);

  useEffect(() => {
    if (!selectedProvider) return;
    if (
      !selectedProvider.supportsThinkingMode ||
      (selectedProvider.thinkingModeRequiresNoReferences &&
        (mode === "character" || referenceFiles.length > 0))
    ) {
      form.setFieldValue("thinkingMode", false);
    }
    if (!selectedProvider.supportsWatermark) form.setFieldValue("watermark", false);
  }, [
    form,
    mode,
    referenceFiles.length,
    selectedProvider,
  ]);

  useEffect(() => {
    if (!latest) return;
    const frame = window.requestAnimationFrame(() => {
      latestResultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [latest]);

  const generate = async (values: GenerationForm) => {
    if (!selectedProviderId) {
      message.error("请先选择生图模型");
      return;
    }
    const payload = new FormData();
    if (generationProviderSelection !== "product-default") {
      payload.append("provider", generationProviderSelection);
    }
    payload.append("characterId", values.characterId);
    payload.append("mode", values.mode);
    payload.append("prompt", values.prompt);
    payload.append("stylePrompt", values.stylePrompt ?? "");
    payload.append("size", values.size);
    payload.append("watermark", String(values.watermark));
    payload.append("thinkingMode", String(values.thinkingMode));
    // 热更新前已经打开的旧表单没有这个字段；不能让 FormData 把 undefined
    // 字符串化后交给后端枚举校验。
    payload.append("referencePurpose", values.referencePurpose ?? "composition");
    payload.append("reason", values.reason);
    const reference = referenceFiles[0]?.originFileObj;
    if (reference) payload.append("reference", reference);

    setSubmitting(true);
    try {
      const result = await api.postForm<GenerationResult>(
        "/api/v1/admin/image-generations",
        payload,
      );
      setLatest(result);
      setReferenceFiles([]);
      await queryClient.invalidateQueries({
        queryKey: ["adminAssetBlob", result.outputAssetId],
      });
      message.success("图片已生成并保存到 OSS，记录也已经入库");
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/image-generations"] });
    } catch (error) {
      message.error((error as Error).message || "生图失败");
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/image-generations"] });
    } finally {
      setSubmitting(false);
    }
  };

  const saveDefaultProvider = async () => {
    if (!defaultProviderDraft) return;
    setSavingDefaultProvider(true);
    try {
      const updated = await api.patch<GenerationConfiguration>(
        "/api/v1/admin/image-generations/configuration",
        { provider: defaultProviderDraft },
      );
      queryClient.setQueryData(["imageGenerationConfiguration"], updated);
      setDefaultProviderDraft(updated.provider);
      message.success(`产品默认绘图模型已切换为${providerLabel(updated.provider)}`);
    } catch (error) {
      message.error((error as Error).message || "保存产品默认绘图模型失败");
    } finally {
      setSavingDefaultProvider(false);
    }
  };

  const retryPersistence = async (row: GenerationRow) => {
    setRetryingPersistenceId(row.id);
    try {
      const result = await api.post<PersistenceRecoveryResult>(
        `/api/v1/admin/image-generations/${row.id}/retry-persistence`,
        {},
      );
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/image-generations"] });
      if (result.status === "running") {
        message.info("图片正在保存到 OSS，请稍候；不会重新调用生图模型");
        return;
      }
      if (result.outputAssetId) {
        await queryClient.invalidateQueries({
          queryKey: ["adminAssetBlob", result.outputAssetId],
        });
      }
      message.success(
        result.alreadyRecovered
          ? "图片已经保存成功，列表已刷新；没有重复计费"
          : "图片已重新下载并保存到 OSS；没有再次调用生图模型，也不会重复计费",
      );
    } catch (error) {
      message.error((error as Error).message || "重新保存图片失败");
      await queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/image-generations"] });
    } finally {
      setRetryingPersistenceId(null);
    }
  };

  const unavailable =
    configuration.data &&
    (!selectedProvider?.configured || !configuration.data.storageConfigured);

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card title="产品绘图设置">
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text type="secondary">
            这里保存的是整个产品的默认绘图模型。用户聊天绘图、失败后重新生成和后台生图都会跟随它；后台仍可为单次任务临时指定其他模型。
          </Typography.Text>
          <Space wrap align="center">
            <Typography.Text>默认模型</Typography.Text>
            <Select<GenerationProviderId>
              aria-label="产品默认绘图模型"
              value={defaultProviderDraft ?? undefined}
              loading={configuration.isLoading}
              disabled={!can("asset.write")}
              onChange={setDefaultProviderDraft}
              style={{ minWidth: 310 }}
              options={configuration.data?.providers.map((provider) => ({
                value: provider.id,
                label: modelOptionLabel(provider),
                disabled: !provider.configured,
              }))}
            />
            <Button
              type="primary"
              loading={savingDefaultProvider}
              disabled={
                !can("asset.write") ||
                !defaultProviderDraft ||
                defaultProviderDraft === configuration.data?.provider
              }
              onClick={() => void saveDefaultProvider()}
            >
              保存为产品默认
            </Button>
            {configuration.data && (
              <Tag color={configuration.data.providerSource === "database" ? "green" : "gold"}>
                {configuration.data.providerSource === "database"
                  ? "后台设置已生效"
                  : "当前使用环境变量兜底"}
              </Tag>
            )}
          </Space>
          <Typography.Text type="secondary">
            供应商的 Access Key 仍只保存在服务端环境变量中，不会写入这项设置。
          </Typography.Text>
        </Space>
      </Card>

      <Card
        title="生图工作台"
        extra={
          configuration.data ? (
            <Space wrap>
              <Typography.Text type="secondary">本次生图模型</Typography.Text>
              <Select<GenerationProviderSelection>
                aria-label="本次生图模型"
                value={generationProviderSelection}
                loading={configuration.isLoading}
                onChange={setGenerationProviderSelection}
                style={{ minWidth: 310 }}
                options={[
                  {
                    value: "product-default",
                    label: `跟随产品默认（${providerLabel(configuration.data.provider)}）`,
                  },
                  ...configuration.data.providers.map((provider) => ({
                    value: provider.id,
                    label: modelOptionLabel(provider),
                  })),
                ]}
              />
              <Tag>约 ¥{selectedProvider?.priceCnyPerImage?.toFixed(2) ?? "-"} / 张</Tag>
            </Space>
          ) : null
        }
      >
        {unavailable && (
          <Alert
            style={{ marginBottom: 16 }}
            type="warning"
            showIcon
            message="生图链路还没有准备完整"
            description={`${providerLabel(selectedProvider?.id ?? "图片供应商")}：${selectedProvider?.configured ? "已连接" : "未配置服务端凭据"}；OSS：${configuration.data?.storageConfigured ? "已连接" : "未配置"}。两者都就绪后才能生成。`}
          />
        )}

        <Form<GenerationForm>
          form={form}
          layout="vertical"
          initialValues={{
            characterId: characterId ?? undefined,
            mode: "character",
            stylePrompt: "",
            size: "2K",
            watermark: false,
            thinkingMode: false,
            referencePurpose: "composition",
          }}
          onFinish={generate}
        >
          <Space align="start" wrap size={20} style={{ width: "100%" }}>
            <Form.Item
              name="characterId"
              label="角色"
              rules={[{ required: true, message: "请选择角色" }]}
              style={{ minWidth: 220 }}
            >
              <Select
                onChange={(value) => setCharacterId(value)}
                options={characters.map((character) => ({
                  value: character.id,
                  label: character.displayName,
                }))}
              />
            </Form.Item>
            <Form.Item name="mode" label="生图模式" rules={[{ required: true }]}>
              <Radio.Group optionType="button" buttonStyle="solid">
                <Radio.Button value="character">画角色本人</Radio.Button>
                <Radio.Button value="general">普通运营图</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item name="size" label="清晰度" rules={[{ required: true }]}>
              <Select
                style={{ width: 120 }}
                options={[
                  { value: "1K", label: "1K" },
                  { value: "2K", label: "2K" },
                  {
                    value: "4K",
                    label: "4K",
                    disabled:
                      !selectedProvider?.supportsReference4K &&
                      (mode === "character" || referenceFiles.length > 0),
                  },
                ]}
              />
            </Form.Item>
          </Space>

          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            showIcon
            message={
              mode === "general"
                ? "普通运营图不自动带任何参考图；服务端会追加与用户聊天相同的统一画风，可另加一张构图、服装、产品或画风参考图。"
                : "角色模式按图1～3带入正面、半侧、侧面三张身份图；附加图为图4。不会自动带CG图，并使用与用户聊天相同的统一画风。"
            }
          />

          <Form.Item
            name="prompt"
            label="画面需求"
            rules={[{ required: true, whitespace: true, message: "请描述要画什么" }]}
          >
            <Input.TextArea
              rows={4}
              maxLength={5_000}
              showCount
              placeholder="例如：缈缈坐在夏夜的窗边，手里捧着发光的灵蝶，横版公众号头图，右侧留标题空间"
            />
          </Form.Item>

          <Form.Item
            name="stylePrompt"
            label="额外画风要求（可选）"
          >
            <Input.TextArea rows={3} maxLength={2_000} showCount />
          </Form.Item>

          <Form.Item label="附加参考图（可选）">
            <Upload
              accept="image/png,image/jpeg,image/webp"
              maxCount={1}
              fileList={referenceFiles}
              beforeUpload={() => false}
              onChange={({ fileList }) => {
                setReferenceFiles(fileList.slice(-1));
                if (
                  fileList.length > 0 &&
                  !selectedProvider?.supportsReference4K &&
                  form.getFieldValue("size") === "4K"
                ) {
                  form.setFieldValue("size", "2K");
                }
              }}
            >
              <Button icon={<UploadOutlined />}>选择图片</Button>
            </Upload>
          </Form.Item>

          {referenceFiles.length > 0 && (
            <Form.Item name="referencePurpose" label="附加参考图用途">
              <div translate="no">
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value="composition">姿势 / 构图 / 服装 / 场景</Radio.Button>
                  <Radio.Button value="style">画风参考</Radio.Button>
                </Radio.Group>
              </div>
            </Form.Item>
          )}

          <Space size={28} wrap>
            <Form.Item
              name="thinkingMode"
              label={
                selectedProvider?.thinkingModeRequiresNoReferences
                  ? "思考模式（仅无参考图）"
                  : "思考模式"
              }
              valuePropName="checked"
            >
              <Switch
                disabled={
                  !selectedProvider?.supportsThinkingMode ||
                  Boolean(
                    selectedProvider?.thinkingModeRequiresNoReferences &&
                    (mode === "character" || referenceFiles.length > 0),
                  )
                }
              />
            </Form.Item>
            <Form.Item name="watermark" label="生成水印" valuePropName="checked">
              <Switch disabled={!selectedProvider?.supportsWatermark} />
            </Form.Item>
          </Space>

          <Form.Item
            name="reason"
            label="用途 / 操作理由"
            rules={[{ required: true, whitespace: true, message: "请写明用途，便于审计" }]}
          >
            <Input
              maxLength={200}
              placeholder="例如：8 月公众号七夕活动头图"
              autoComplete="off"
              spellCheck={false}
              translate="no"
              data-ms-editor="false"
            />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            icon={<PictureOutlined />}
            loading={submitting}
            disabled={!can("asset.write") || !selectedProviderId || Boolean(unavailable)}
          >
            {submitting ? "图片生成中，请勿关闭页面" : "生成并保存"}
          </Button>
        </Form>
      </Card>

      {latest && (
        <div ref={latestResultRef}>
          <Card title="本次生成结果" extra={<Tag color="success">已进生图记录</Tag>}>
            <Space align="start" size={16} wrap>
              <AuthenticatedAssetImage
                assetId={latest.outputAssetId}
                publicUrl={latest.outputAssetUrl}
                width={240}
              />
              <Space direction="vertical">
                <Typography.Text>模型：{latest.model}</Typography.Text>
                <Typography.Text>供应商：{providerLabel(latest.provider)}</Typography.Text>
                <Typography.Text>
                  绘图模型耗时：{(latest.latencyMs / 1_000).toFixed(1)} 秒
                </Typography.Text>
                <Typography.Text>
                  整个请求耗时：{(latest.totalLatencyMs / 1_000).toFixed(1)} 秒
                </Typography.Text>
                <Typography.Text>成本：¥{(latest.costCents / 100).toFixed(2)}</Typography.Text>
                <Typography.Text type="secondary">记录 ID：{latest.id}</Typography.Text>
                {latest.providerRequestId && (
                  <Typography.Text type="secondary">
                    供应商任务 / 请求 ID：{latest.providerRequestId}
                  </Typography.Text>
                )}
              </Space>
            </Space>
          </Card>
        </div>
      )}

      <DataTable<GenerationRow>
        title="生图记录"
        path="/api/v1/admin/image-generations"
        fixedParams={characterId ? { characterId } : undefined}
        filterKeys={FILTERS}
        expandable={{
          expandedRowRender: (row) => (
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="实际发送给模型的 Prompt">
                <Typography.Paragraph copyable style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {row.effectivePrompt ?? "—"}
                </Typography.Paragraph>
              </Descriptions.Item>
              <Descriptions.Item label="参考图快照">
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                  {JSON.stringify(row.referenceInputs, null, 2)}
                </pre>
              </Descriptions.Item>
              <Descriptions.Item label="参数">
                <pre style={{ margin: 0 }}>{JSON.stringify(row.settings, null, 2)}</pre>
              </Descriptions.Item>
              <Descriptions.Item label="供应商任务 / 请求 ID">
                {row.providerRequestId ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="供应商计量">
                <pre style={{ margin: 0 }}>{JSON.stringify(row.providerMetadata, null, 2)}</pre>
              </Descriptions.Item>
            </Descriptions>
          ),
        }}
        toolbar={
          <Space wrap>
            <Select
              allowClear
              style={{ width: 140 }}
              placeholder="发起方"
              value={searchParams.get("requesterKind") ?? undefined}
              onChange={(value) => setParam("requesterKind", value)}
              options={[
                { value: "admin", label: "管理员" },
                { value: "user", label: "用户" },
                { value: "system", label: "系统" },
              ]}
            />
            <Select
              allowClear
              style={{ width: 140 }}
              placeholder="模式"
              value={searchParams.get("mode") ?? undefined}
              onChange={(value) => setParam("mode", value)}
              options={[
                { value: "character", label: "角色本人" },
                { value: "general", label: "普通生图" },
              ]}
            />
            <Select
              allowClear
              style={{ width: 140 }}
              placeholder="状态"
              value={searchParams.get("status") ?? undefined}
              onChange={(value) => setParam("status", value)}
              options={["queued", "running", "succeeded", "failed"].map((value) => ({
                value,
                label: value,
              }))}
            />
          </Space>
        }
        columns={[
          {
            title: "结果",
            width: 88,
            render: (_, row) =>
              row.outputAsset ? (
                <AuthenticatedAssetImage
                  assetId={row.outputAsset.id}
                  publicUrl={row.outputAsset.publicUrl}
                />
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              ),
          },
          {
            title: "时间",
            dataIndex: "createdAt",
            width: 120,
            render: (value: string) => <RelativeTime value={value} />,
          },
          {
            title: "来源",
            width: 130,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Tag color={row.requesterKind === "admin" ? "blue" : undefined}>
                  {row.requesterKind === "admin" ? "管理员" : row.requesterKind}
                </Tag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {row.requesterName ?? row.requesterUserId ?? "—"}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: "模式",
            dataIndex: "mode",
            width: 100,
            render: (value: GenerationRow["mode"]) =>
              value === "character" ? "角色本人" : "普通生图",
          },
          {
            title: "状态",
            dataIndex: "status",
            width: 100,
            render: (value: string) => <StatusTag value={value} />,
          },
          {
            title: "画面需求",
            dataIndex: "requestPrompt",
            width: 320,
            render: (value: string) => (
              <Typography.Paragraph ellipsis={{ rows: 3, expandable: true, symbol: "展开" }} style={{ margin: 0 }}>
                {value}
              </Typography.Paragraph>
            ),
          },
          {
            title: "模型 / 成本",
            width: 230,
            render: (_, row) => (
              <Space direction="vertical" size={0}>
                <Typography.Text>{row.model}</Typography.Text>
                <Typography.Text type="secondary">
                  {providerLabel(row.provider)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  ¥{(row.costCents / 100).toFixed(2)}
                </Typography.Text>
                <Typography.Text type="secondary">
                  模型 {(row.latencyMs / 1_000).toFixed(1)}s · 非模型{" "}
                  {(Math.max(0, row.totalLatencyMs - row.latencyMs) / 1_000).toFixed(1)}s
                </Typography.Text>
                <Typography.Text type="secondary">
                  全程{" "}
                  {row.totalLatencyMs > 0 ? `${(row.totalLatencyMs / 1_000).toFixed(1)}s` : "—"}
                </Typography.Text>
                {wasPersistenceRecovered(row.providerMetadata) && (
                  <Tag color="orange">含一次保存失败后的无计费恢复</Tag>
                )}
              </Space>
            ),
          },
          {
            title: "错误",
            width: 220,
            render: (_, row) => (
              <Space direction="vertical" size={6}>
                {row.errorMessage ? (
                  <Typography.Text type="danger">
                    {row.errorCode}: {row.errorMessage}
                  </Typography.Text>
                ) : (
                  "—"
                )}
                {row.recoveryAvailable && can("asset.write") && (
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={retryingPersistenceId === row.id}
                    disabled={retryingPersistenceId !== null && retryingPersistenceId !== row.id}
                    onClick={() => void retryPersistence(row)}
                  >
                    只重试保存（不重新生图）
                  </Button>
                )}
                {row.recoveryInProgress && (
                  <Tag color="processing">正在保存到 OSS，请稍候</Tag>
                )}
              </Space>
            ),
          },
          {
            title: "用途",
            dataIndex: "reason",
            width: 180,
          },
        ]}
      />
    </Space>
  );
}
